import React, { useState, useEffect } from 'react';
import { ui } from '../../../config/uiSizes';

interface FlowLiteralReplaceParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

/**
 * Find-and-replace on hand-typed literal values *inside* Architect flow logic (e.g. a queue name
 * typed as a literal in an Update Data action). Renaming a resource does not touch these embedded
 * strings, so this transform rewrites them directly in the flow YAML. Sent to the backend as a
 * `FlowLiteralReplace` transformation with `find`/`replace` parameters.
 */
export const FlowLiteralReplaceParameters: React.FC<FlowLiteralReplaceParametersProps> = ({
  parameters,
  onParametersChange
}) => {
  const [find, setFind] = useState<string>((parameters?.['find'] as string) || '');
  const [replace, setReplace] = useState<string>((parameters?.['replace'] as string) || '');

  useEffect(() => {
    if (onParametersChange) {
      onParametersChange({ find, replace });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [find, replace]);

  return (
    <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
      <div className={`${ui.mb.title}`}>
        <h3 className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100`}>Flow Literal Replace</h3>
        <p className={`${ui.text.label} text-gray-600 dark:text-gray-400 mt-1`}>
          Replace hand-typed text inside Architect flows (e.g. a queue name set as a literal in an Update Data action). Renaming resources does not update these embedded values.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Find text <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder="TEST-"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={` ${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Case-sensitive. Matched inside flow values only — keys and structure are never changed. Example: "TEST-".
          </p>
        </div>

        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Replace with <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={replace}
            onChange={(e) => setReplace(e.target.value)}
            placeholder="UAT-"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={` ${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Text to substitute in. Example: "UAT-".
          </p>
        </div>
      </div>
    </div>
  );
};

export default FlowLiteralReplaceParameters;
